/**
 * Prompt Templates on the JWT Surface group `prompts` (spec FSI #1226).
 * Shapes follow the run's Xano contract for the #593 reads.
 */

export type PromptScope = 'vh3' | 'team' | 'personal';

/** Agent ids a template targets: 1 Connie, 2 Tia, 4 Salma. */
export const PROMPT_AGENT_IDS = { connie: 1, tia: 2, salma: 4 } as const;

export type PromptAgent = keyof typeof PROMPT_AGENT_IDS;

export type PromptVariableRecord = {
  name: string;
  description?: string;
  default?: string;
};

/**
 * A Prompt Group's type of work: the colour its tag takes, from the agent
 * palette (ops Connie, technical Tia, sales Salma; the rest for later).
 * Xano answers `ops` when a group has none.
 */
export const PROMPT_WORK_TYPES = [
  'ops',
  'technical',
  'sales',
  'performance',
  'safety',
  'training',
] as const;

export type PromptWorkType = (typeof PROMPT_WORK_TYPES)[number];

export type PromptGroupRef = {
  id: number;
  label: string;
  highlight: string;
  /** Lucide name. */
  icon: string;
  scope: PromptScope;
  work_type?: PromptWorkType;
};

/** A Prompt Template as `GET prompts/templates` returns it. */
export type PromptTemplateRecord = {
  id: number;
  scope: PromptScope;
  mine: boolean;
  owner_user_id: number;
  group: PromptGroupRef | null;
  group_id: number;
  agent_ids: number[];
  origin_template_id: number;
  origin_version: number;
  origin_current_version: number;
  hidden: boolean;
  archived: boolean;
  sort_order: number;
  version: number;
  version_id: number;
  title: string;
  body: string;
  variables: PromptVariableRecord[];
  saved_by: { id: number; name: string } | null;
  saved_at: number;
  created_at: number;
  updated_at: number;
};

/** A Prompt Group as `GET prompts/groups` returns it. */
export type PromptGroupRecord = {
  id: number;
  scope: PromptScope;
  mine: boolean;
  label: string;
  highlight: string;
  /** Lucide name. */
  icon: string;
  icon_url?: string;
  /** On a VH3 group a tenant override's type wins when one is set. */
  work_type?: PromptWorkType;
  sort_order: number;
  origin_group_id: number;
  hidden: boolean;
  override_id: number;
};

/** `GET prompts/config`: the tenant switch and the caller's team rights. */
export type PromptsConfig = {
  enabled: boolean;
  can_edit_team: boolean;
  user_id: number;
};

export type ListPromptTemplatesParams = {
  agent_id?: number;
  group_id?: number;
  scope?: PromptScope;
  archived?: boolean;
  search?: string;
};

/** `POST prompts/templates` (#594): a personal template unless `scope` says team. */
export type CreatePromptTemplateBody = {
  title: string;
  body: string;
  /** Only description/default per name; the server reads the names from the body. */
  variables?: PromptVariableRecord[];
  /** Omit for My prompts. */
  group_id?: number;
  agent_ids: number[];
  scope?: 'personal' | 'team';
};

/**
 * `PATCH prompts/templates/{id}` (#594): title, body and variables save a new
 * Template Version; the rest edit the row. `group_id` 0 is My prompts.
 */
export type UpdatePromptTemplateBody = Partial<{
  title: string;
  body: string;
  variables: PromptVariableRecord[];
  group_id: number;
  agent_ids: number[];
  sort_order: number;
}>;

/** `POST prompts/templates/{id}/archive` and `/unarchive`. */
export type PromptArchiveResult = { id: number; archived: boolean };

/** `POST prompts/templates/{id}/hide` and `/unhide` (VH3 defaults, per tenant). */
export type PromptHiddenResult = { id: number; hidden: boolean };

/** `POST prompts/groups` (#595): a personal group unless `scope` says team. */
export type CreatePromptGroupBody = {
  /** 1–40. */
  label: string;
  /** The drill-in word, 1–40. */
  highlight: string;
  /** Lucide name. */
  icon: string;
  /** Defaults to `ops`. */
  work_type?: PromptWorkType;
  sort_order?: number;
  scope?: 'personal' | 'team';
};

/**
 * `PATCH prompts/groups/{id}` (#595). On a VH3 group the change is stored on
 * the tenant's override row; the VH3 row never changes.
 */
export type UpdatePromptGroupBody = Partial<{
  label: string;
  highlight: string;
  icon: string;
  work_type: PromptWorkType;
  sort_order: number;
}>;

/** What `PATCH prompts/groups/{id}` returns. */
export type PromptGroupWriteResult = {
  id: number;
  scope: PromptScope;
  label: string;
  highlight: string;
  icon: string;
  work_type?: PromptWorkType;
  sort_order: number;
};

/** One Template Version, as `GET prompts/templates/{id}/versions` (#596) lists it. */
export type PromptTemplateVersionRecord = {
  id: number;
  version: number;
  title: string;
  body: string;
  variables: PromptVariableRecord[];
  /** `name` is "VH3" for seeds. */
  saved_by: { id: number; name: string } | null;
  saved_at: number;
  /** e.g. "edited", "restored from v2", "copied from VH3 v1", "took VH3 update v2". */
  note: string;
  current: boolean;
};

/** `GET prompts/templates/{id}/versions`: newest first. */
export type PromptVersionsResponse = {
  template_id: number;
  current_version: number;
  items: PromptTemplateVersionRecord[];
};

/** `POST prompts/templates/{id}/keep-mine`: equal versions clear the notice. */
export type PromptKeepMineResult = {
  id: number;
  origin_version: number;
  origin_current_version: number;
};

/** One row of `GET prompts/usage`: Prompt Use rows across users and agents. */
export type PromptUsageRecord = {
  template_id: number;
  /** Every use in the tenant. */
  count: number;
  /** The caller's own uses. */
  mine: number;
};

/** `GET prompts/recent`: a Template with the caller's last use of it. */
export type PromptRecentTemplateRecord = PromptTemplateRecord & {
  last_used_at: number;
};

/**
 * What a chat is about, for Context Variables (spec FSI #1230): the Job in
 * Job chat, the Library Document in Ask Tia, the Quote Document in Ask Salma.
 * Global chat and the older document and quote chats have none.
 */
export type PromptContextSubject =
  | { kind: 'job'; id: number }
  | { kind: 'document'; id: string }
  | { kind: 'quote'; id: string };

export type PromptContextSubjectKind = PromptContextSubject['kind'];

/** The chats a Context Variable fills in; `everywhere` is every chat. */
export type PromptContextSource = PromptContextSubjectKind | 'everywhere';

/** Editor groups, in the Insert context menu's order. */
export const PROMPT_CONTEXT_GROUPS = [
  'Job',
  'Customer',
  'Site',
  'Document',
  'Quote',
  'Everywhere',
] as const;

export type PromptContextGroup = (typeof PROMPT_CONTEXT_GROUPS)[number];

/** One name on VH3's list of Context Variables, as Xano fn 2106 holds it. */
export type PromptContextName = {
  /** Without braces: `job.reference`, `today`. */
  name: string;
  /** Plain label: "Job reference". */
  label: string;
  group: PromptContextGroup;
  subjects: PromptContextSource[];
  /** Copy for authors: "Job chat", "Ask Tia", "Every chat". */
  fills_in: string;
  hint: string;
};

/**
 * `GET prompts/context`: the list and the values found for one subject. Only
 * found values have a key. With the tenant switch off every part is empty.
 */
export type PromptContextResponse = {
  subject: PromptContextSubjectKind | null;
  names: PromptContextName[];
  values: Record<string, string>;
};
